import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';

import {
    CompNavItem,
    HomeAwayNavItem,
    OtherMatchesToggle,
    ResponsiveHomeAwayNavItem,
    CalendarButton,
    ResponsiveSeasonNavItem
} from 'common/react/components/competition-nav/nav-items';

import { navSelector } from 'store/modules/competition-nav/selectors/nav-selector';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useWindowSize } from 'common/react/hooks/use-window-resize';
import { useFilterSeasonsSelector } from 'common/react/components/competition-nav/hooks/use-filter-seasons-selector';
import { useGetCurrentCompFilters } from 'common/react/components/competition-nav/hooks/use-get-current-comp-filters';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { FilterDropdown } from 'widgets/filters/js/components/FilterDropdown';
import { FilterButton } from 'common/react/components/FilterButton';
import { ResetFilters } from 'widgets/statspro/js/components/common/stats-section/StatsSectionNav/ResetFilters';
import { initialiseNav } from 'common/store/modules/competition-nav/thunks';
import { useGetUrlParams } from 'common/react/components/competition-nav/hooks/use-get-url-params';
import { useSyncUrlParams } from 'common/react/hooks/use-sync-url-params';
import { getTranslation } from 'common/react/utils/translations';

export const FixturesNav = ({
    competitionId,
    seasonId,
    ecalId,
    calendarLinkUrl,
    calendarLinkText = getTranslation('label.calendar.link.text'),
    calendarLinkIcon = getTranslation('label.calendar.link.icon'),
    title
}) => {
    const dispatch = useDispatch();

    const windowSize = useWindowSize();
    const [areFilterOptionsVisible, setAreFilterOptionsVisible] =
        useState(false);

    const isResponsive = () =>
        windowSize.width < PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    const nav = useSelector(navSelector);
    const options = useCompetitionNavigationOptions(nav);

    const params = useGetUrlParams();

    const initialValues = {
        competitionId: params.competition || competitionId,
        seasonId: params.season || seasonId,
        roundId: params.round || '',
        filters: {
            teams: params.teams || [],
            venues: params.venues || [],
            cities: params.cities || []
        }
    };

    useEffect(() => {
        dispatch(
            initialiseNav({
                initialValues: initialValues
            })
        );
    }, [dispatch]);

    useSyncUrlParams({
        selector: navSelector,
        mapToParams: (items) => ({
            Competition: items.competition.id,
            Season: items.season.id,
            Round: items.round.id,
            Teams: items.filters.teams,
            Venues: items.filters.venues,
            Cities: items.filters.cities
        })
    });

    const { competitions, seasons } = options;

    const seasonsFiltered = useFilterSeasonsSelector({ competitions, seasons });
    const currentFilters = useGetCurrentCompFilters();
    const competitionNavRef = useRef();

    const hasSeasonChanged = useCallback(() => {
        return nav.season.id !== Number.parseInt(nav.season.initialItemId);
    }, [nav.season.id, nav.season.initialItemId]);

    const isValidCalendarButton = ecalId || calendarLinkUrl;

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title,
        filtersState: nav,
        options,
        filtersFields: ['competition', 'season', 'homeaway', 'otherMatches'],
        filtersRef: competitionNavRef
    });

    const renderDesktop = (
        <>
            <StatsSection.ScrollableItems>
                <CompNavItem
                    classes="fixtures-nav__comp-select"
                    competitions={competitions}
                    hideLabel={false}
                />
                <FilterDropdown
                    title={title}
                    icon="season-trophy"
                    navItem="season"
                    items={seasonsFiltered}
                />
                <HomeAwayNavItem title={title} />
                <ResetFilters
                    classes={'u-margin-right-auto'}
                    competitionId={competitionId}
                    seasonId={seasonId}
                />
            </StatsSection.ScrollableItems>
            <StatsSection.NavItems>
                <OtherMatchesToggle classes={'no-border'} />

                {isValidCalendarButton &&
                    (calendarLinkUrl ? (
                        <CalendarButton
                            classes="calendar-button"
                            calendarLinkUrl={calendarLinkUrl}
                            calendarLinkIcon={calendarLinkIcon}
                            calendarLinkText={calendarLinkText}
                        />
                    ) : (
                        <CalendarButton
                            classes="calendar-button"
                            ecalId={ecalId}
                            calendarLinkIcon={calendarLinkIcon}
                            calendarLinkText={calendarLinkText}
                        />
                    ))}
            </StatsSection.NavItems>
        </>
    );

    const renderMobile = (
        <>
            <StatsSection.NavItems>
                <CompNavItem
                    classes="fixtures-nav__comp-select"
                    competitions={competitions}
                    hideLabel={true}
                />
                <FilterButton
                    classes="fixtures-nav__filter-button"
                    onChange={() => setAreFilterOptionsVisible((prev) => !prev)}
                    areFilterOptionsVisible={areFilterOptionsVisible}
                    showFilterLabel={false}
                    showActiveCircle
                    isFilterSelectedCustom={hasSeasonChanged}
                />
                {isValidCalendarButton &&
                    (calendarLinkUrl ? (
                        <CalendarButton
                            classes="fixtures-nav__calendar-button stats-leaders-nav__button no-border"
                            calendarLinkUrl={calendarLinkUrl}
                            calendarLinkIcon={calendarLinkIcon}
                            calendarLinkText={calendarLinkText}
                        />
                    ) : (
                        <CalendarButton
                            classes="fixtures-nav__calendar-button stats-leaders-nav__button no-border"
                            ecalId={ecalId}
                            calendarLinkIcon={calendarLinkIcon}
                            calendarLinkText={calendarLinkText}
                        />
                    ))}
                <OtherMatchesToggle
                    classes="fixtures-nav__other-matches-toggle"
                    labelRightPosition
                />
            </StatsSection.NavItems>
            {currentFilters.competitionId &&
                currentFilters.compSeason?.id &&
                areFilterOptionsVisible && (
                    <StatsSection.NavFilterItems>
                        <ResponsiveSeasonNavItem
                            seasons={seasonsFiltered}
                            title={title}
                            hideLabel
                        />
                        <ResponsiveHomeAwayNavItem title={title} />
                    </StatsSection.NavFilterItems>
                )}
        </>
    );

    return (
        <div
            className="competition-nav filter-dropdown"
            ref={competitionNavRef}
        >
            <StatsSection.Main>
                <StatsSection.Nav
                    withScrollableFilter={!showResponsiveItems}
                    useRefinedFilter
                >
                    {showResponsiveItems ? renderMobile : renderDesktop}
                </StatsSection.Nav>
            </StatsSection.Main>
        </div>
    );
};

FixturesNav.propTypes = {
    competitionId: PropTypes.string,
    seasonId: PropTypes.string,
    ecalId: PropTypes.string,
    calendarLinkUrl: PropTypes.string,
    calendarLinkText: PropTypes.string,
    calendarLinkIcon: PropTypes.string,
    title: PropTypes.string
};
